"use client";

import { useMemo } from "react";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const NONE_VALUE = "__none__";

export type QuickBooksAccountOption = {
  Id?: string;
  id?: string;
  Name?: string;
  name?: string;
  AccountType?: string;
  accountType?: string;
};

function accountId(account: QuickBooksAccountOption): string {
  return String(account.Id ?? account.id ?? "");
}

function accountName(account: QuickBooksAccountOption): string {
  return String(account.Name ?? account.name ?? accountId(account));
}

function accountType(account: QuickBooksAccountOption): string {
  return String(account.AccountType ?? account.accountType ?? "");
}

export function formatQuickBooksAccountLabel(account: QuickBooksAccountOption): string {
  const type = accountType(account);
  const name = accountName(account);
  return type ? `${name} · ${type}` : name;
}

export interface QuickBooksAccountSelectorProps {
  accounts: QuickBooksAccountOption[];
  value: string;
  onValueChange: (accountId: string, account?: QuickBooksAccountOption) => void;
  accountTypeFilter?: string[];
  allowNone?: boolean;
  noneLabel?: string;
  placeholder?: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

export function QuickBooksAccountSelector({
  accounts,
  value,
  onValueChange,
  accountTypeFilter,
  allowNone = false,
  noneLabel = "None",
  placeholder = "Select QuickBooks account",
  label,
  id,
  disabled,
  className,
}: QuickBooksAccountSelectorProps) {
  const filteredAccounts = useMemo(() => {
    const all = (accounts ?? []).filter((account) => accountId(account));
    if (!accountTypeFilter?.length) return all;
    const allowed = new Set(accountTypeFilter.map((t) => t.toLowerCase()));
    const filtered = all.filter((account) =>
      allowed.has(accountType(account).toLowerCase())
    );
    if (value) {
      const selected = all.find((account) => accountId(account) === value);
      if (selected && !filtered.some((a) => accountId(a) === value)) {
        return [...filtered, selected];
      }
    }
    return filtered;
  }, [accounts, accountTypeFilter, value]);

  const selectValue = value || (allowNone ? NONE_VALUE : undefined);

  return (
    <div className={cn("grid gap-2", className)}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <Select
        value={selectValue}
        onValueChange={(next) => {
          if (next === NONE_VALUE) {
            onValueChange("");
            return;
          }
          const account = filteredAccounts.find((a) => accountId(a) === next);
          onValueChange(next, account);
        }}
        disabled={disabled}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowNone ? <SelectItem value={NONE_VALUE}>{noneLabel}</SelectItem> : null}
          {filteredAccounts.map((account) => {
            const idValue = accountId(account);
            return (
              <SelectItem key={idValue} value={idValue}>
                {formatQuickBooksAccountLabel(account)}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
